<template>
  <header class="header" :class="{ 'feed-open': isFeedOpen }">
    <div v-if="showHeaderAnnouncement" class="header__announcement">
      <Announcement />
    </div>
    <div class="header__announcement-bar">
      <nav class="secondary-nav" :aria-label="t('HEADER.A11Y.secondaryNavigation')">
        <router-link class="secondary-nav__link" :to="{ name: RouteName.ALPHA_WHALES }">
          <i class="fa-solid fa-gem"></i>
          {{ t('HEADER.DROPDOWN_MENU.other.alphaWhalesClub') }}
        </router-link>
        <router-link class="secondary-nav__link" :to="{ name: RouteName.PROVABLY_FAIR }">
          <i class="fa-solid fa-scale-balanced"></i>
          {{ t('HEADER.DROPDOWN_MENU.other.provablyFair') }}
        </router-link>
        <router-link class="secondary-nav__link" :to="{ name: RouteName.SUPPORT }">
          <i class="fa-solid fa-headset"></i>
          {{ t('HEADER.DROPDOWN_MENU.other.support') }}
        </router-link>
        <a
          class="secondary-nav__link hidden-mobile"
          @click.prevent="toggleActivityFeed"
          href="#"
          :title="t('HEADER.ACTIVITY_FEED')"
        >
          <i class="fa-solid fa-bolt" :class="{ 'text-yellow-400': isFeedOpen }"></i>
          {{ t('HEADER.FEED') }}
          <div v-if="hasUnread" class="nav-notification-dot"></div>
        </a>
        <a
          class="secondary-nav__link"
          href="https://discord.gg/veXFs2xstD"
          target="_blank"
          rel="noopener noreferrer"
          :title="t('HEADER.DROPDOWN_MENU.other.discord')"
        >
          <i class="fa-brands fa-discord"></i>
          {{ t('HEADER.DROPDOWN_MENU.other.discord') }}
        </a>
      </nav>
    </div>

    <div class="main-navigation">
      <!-- Logo -->
      <router-link :to="{ name: RouteName.CASES }" class="logo" :aria-label="t('HEADER.HOME')">
        <!-- Normal Logos -->
        <!-- <img src="@/assets/SKINWHALES.svg" alt="SkinWhales Logo" />
        <img src="@/assets/logo-wo-text.png" alt="SkinWhales Logo" class="logo-mobile" /> -->

        <img src="@/assets/SKINWHALES.svg" alt="" class="logo-desktop" />
        <img src="@/assets/logo-wo-text.png" alt="" class="logo-mobile" />
      </router-link>
      <!-- Nav -->
      <nav :aria-label="t('HEADER.A11Y.mainNavigation')">
        <div class="routes desktop-routes">
          <router-link
            v-for="r in mainNavRoutes"
            :key="r.name"
            :to="{ name: r.name }"
            class="route"
            :title="r.label"
            :aria-label="r.label"
          >
            <i :class="['icon', r.icon]"></i>
            <span class="label">{{ r.label }}</span>
            <ZeroEdgeBadge v-if="SHOW_ZERO_EDGE_MARKETING && isUpgradeRoute(r.name)" class="route-badge" />
          </router-link>
        </div>

        <div class="right-navigation">
          <div v-if="profileLoading" class="profile-area">
            <WalletSkeleton />
            <LanguageChanger />
          </div>
          <div v-else-if="profile" class="profile-area">
            <Wallet />
            <LanguageChanger />

            <router-link
              :to="{ name: 'profile' }"
              :title="t('HEADER.A11Y.goToProfile')"
              class="profile-link"
              :class="{ 'profile-link--vip': profile.isVip }"
            >
              <img class="avatar" :src="profile.avatar" alt="" />
              <div class="level-badge">
                {{ profile.currentLevel }}
              </div>
            </router-link>

            <div
              class="dropdown-menu-toggle"
              ref="toggleEl"
              :aria-expanded="menuOpen ? 'true' : 'false'"
              aria-controls="userMenu"
              role="button"
              tabindex="0"
              @click.stop="toggleMenu"
              @keydown.enter.prevent="toggleMenu"
              @keydown.space.prevent="toggleMenu"
            >
              <i class="fa-solid fa-bars"></i>
            </div>

            <!-- Dropdown -->
            <DropdownMenu
              :menuOpen="menuOpen"
              :handleLogout="handleLogout"
              :closeMenu="closeMenu"
              ref="menuEl"
              @closeMenu="closeMenu"
            />
          </div>
          <a
            class="login-with-steam"
            v-else
            @click.prevent="openLoginModal"
            :title="t('HEADER.LOGIN_WITH_STEAM')"
          >
            <i class="fa-brands fa-steam"></i>
            <span>{{ t('HEADER.LOGIN_WITH_STEAM') }}</span>
          </a>
        </div>
      </nav>
    </div>

    <!-- Desktop Game Navigation -->
    <nav class="game-navigation" :aria-label="t('HEADER.A11Y.gameNavigation')">
      <router-link
        v-for="r in gameNavRoutes"
        :key="r.name"
        :to="{ name: r.name }"
        class="game-route"
        :title="r.label"
        :aria-label="r.label"
      >
        <i :class="['icon', r.icon]" aria-hidden="true"></i>
        <span class="label">{{ r.label }}</span>
        <ZeroEdgeBadge v-if="SHOW_ZERO_EDGE_MARKETING && isUpgradeRoute(r.name)" class="route-badge" />
      </router-link>
    </nav>
  </header>

  <!-- Mobile Bottom Navigation -->
  <nav
    class="mobile-bottom-nav"
    :aria-label="t('HEADER.A11Y.mobileNavigation')"
    :class="{ 'feed-open': isFeedOpen }"
  >
    <router-link
      v-for="r in primaryNavRoutes"
      :key="r.name"
      :to="{ name: r.name }"
      class="mobile-nav-item"
      :aria-label="r.label"
      @click="closeMobileMenu"
    >
      <i :class="['icon', r.icon]" aria-hidden="true"></i>
      <span class="label">{{ r.label }}</span>
      <ZeroEdgeBadge
        v-if="SHOW_ZERO_EDGE_MARKETING && isUpgradeRoute(r.name)"
        class="mobile-route-badge"
        compact
      />
    </router-link>
    <button
      v-if="secondaryNavRoutes.length > 0"
      class="mobile-nav-item mobile-nav-more"
      @click="toggleMobileMenu"
      :aria-label="t('HEADER.A11Y.moreNavigationItems')"
    >
      <i class="icon fa-solid fa-ellipsis" aria-hidden="true"></i>
      <span class="label">{{ t('HEADER.MORE') }}</span>
    </button>
  </nav>

  <!-- Mobile Menu Overlay -->
  <Transition name="mobile-menu">
    <div v-if="mobileMenuOpen" class="mobile-menu-overlay" @click="closeMobileMenu">
      <div class="mobile-menu-content" @click.stop>
        <div class="mobile-menu-header">
          <h3>{{ t('HEADER.NAVIGATION') }}</h3>
          <button
            class="close-btn"
            @click="closeMobileMenu"
            :aria-label="t('HEADER.A11Y.closeMenu')"
          >
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="mobile-menu-items">
          <router-link
            v-for="r in secondaryNavRoutes"
            :key="r.name"
            :to="{ name: r.name }"
            class="mobile-menu-item"
            @click="closeMobileMenu"
          >
            <i :class="['icon', r.icon]" aria-hidden="true"></i>
            <span class="label">{{ r.label }}</span>
            <ZeroEdgeBadge
              v-if="SHOW_ZERO_EDGE_MARKETING && isUpgradeRoute(r.name)"
              class="mobile-menu-badge"
              size="md"
            />
          </router-link>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useAppStore } from '@/store/app'
import { useProfileStore } from '@/store/profile'
import { useFeatureFlagsStore } from '@/store/featureFlags'
import { usePromotionalEventsStore } from '@/store/promotionalEvents'
import { useActivityFeedStore } from '@/store/activityFeed'
import { FeatureFlag } from '@/types/featureFlags'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
// Components
import Wallet from './components/Wallet.vue'
import WalletSkeleton from './components/WalletSkeleton.vue'
import Announcement from './components/Announcement.vue'
import DropdownMenu from './components/DropdownMenu.vue'
import LanguageChanger from './components/LanguageChanger.vue'
import ZeroEdgeBadge from '@/components/ZeroEdgeBadge/ZeroEdgeBadge.vue'
import { RouteName } from '@/router/types'
import { SHOW_ZERO_EDGE_MARKETING } from '@/utils/upgrade'

defineOptions({ name: 'Header' })
const { t } = useI18n()
const profileStore = useProfileStore()
const appStore = useAppStore()
const featureFlagsStore = useFeatureFlagsStore()
const promotionalEventsStore = usePromotionalEventsStore()
const activityFeedStore = useActivityFeedStore()

const { profile, profileLoading } = storeToRefs(profileStore)
const { announcement } = storeToRefs(appStore)
const { hasActiveEvent } = storeToRefs(promotionalEventsStore)
const { isOpen: isFeedOpen, hasUnread } = storeToRefs(activityFeedStore)

function toggleActivityFeed() {
  activityFeedStore.toggleFeed()
}

const showHeaderAnnouncement = computed(() => !isFeedOpen.value && Boolean(announcement?.value))

// Feature flag checks
const isDailyFreeEnabled = computed(() =>
  featureFlagsStore.isEnabled(FeatureFlag.GAME_CASES_DAILYFREE),
)
const isCasesEnabled = computed(() => featureFlagsStore.isEnabled(FeatureFlag.GAME_CASES))
const isArcadeEnabled = computed(() =>
  featureFlagsStore.isEnabled(FeatureFlag.GAME_ARCADE_WORDFRAG),
)
const isGiveawaysEnabled = computed(() => featureFlagsStore.isEnabled(FeatureFlag.GAME_GIVEAWAYS))
const isEventsEnabled = computed(() => featureFlagsStore.isEnabled(FeatureFlag.GAME_EVENTS))
const isUpgradeEnabled = computed(() => featureFlagsStore.isEnabled(FeatureFlag.GAME_UPGRADE))
const isArenaEnabled = computed(() => featureFlagsStore.isEnabled(FeatureFlag.GAME_ARENA))
const isContractsEnabled = computed(() => {
  const flagName = FeatureFlag.GAME_CONTRACTS
  const hasExplicitToggle = Object.prototype.hasOwnProperty.call(featureFlagsStore.flags, flagName)
  return hasExplicitToggle ? featureFlagsStore.isEnabled(flagName) : true
})
const isSkinClashEnabled = computed(() => {
  const flagName = FeatureFlag.GAME_SKIN_CLASH
  const hasExplicitToggle = Object.prototype.hasOwnProperty.call(featureFlagsStore.flags, flagName)
  return hasExplicitToggle ? featureFlagsStore.isEnabled(flagName) : true
})

const gameRouteNames = new Set<string>([
  RouteName.CASES,
  RouteName.ARENA,
  RouteName.SKIN_CLASH,
  RouteName.UPGRADE,
  RouteName.CONTRACTS,
  RouteName.ARCADE,
  RouteName.WHALE_PROTOCOL,
])
const gameRouteOrder = [
  RouteName.CASES,
  RouteName.ARENA,
  RouteName.SKIN_CLASH,
  RouteName.UPGRADE,
  RouteName.CONTRACTS,
  RouteName.ARCADE,
  RouteName.WHALE_PROTOCOL,
]

// Navigation routes filtered by feature flags
const navRoutes = computed(() => {
  const allRoutes: Array<{ name: string; label: string; icon: string }> = []

  if (isDailyFreeEnabled.value) {
    allRoutes.push({
      name: RouteName.DAILY_FREE,
      label: t('HEADER.NAV.dailyFree'),
      icon: 'fa-solid fa-calendar-day',
    })
  }

  if (isGiveawaysEnabled.value) {
    allRoutes.push({
      name: RouteName.GIVEAWAYS,
      label: t('HEADER.NAV.giveaways'),
      icon: 'fa-solid fa-gift',
    })
  }

  if (isEventsEnabled.value) {
    allRoutes.push({
      name: RouteName.EVENTS,
      label: t('HEADER.NAV.events'),
      icon: 'fa-brands fa-web-awesome',
    })
    if (hasActiveEvent.value) {
      allRoutes.push({
        name: RouteName.TIDES_OF_WAR,
        label: t('HEADER.NAV.tidesOfWar'),
        icon: 'fa-solid fa-crosshairs',
      })
    }
  }

  if (isArcadeEnabled.value) {
    allRoutes.push({
      name: RouteName.ARCADE,
      label: t('HEADER.NAV.arcade'),
      icon: 'fa-solid fa-gamepad',
    })
  }

  if (isArenaEnabled.value) {
    allRoutes.push({
      name: RouteName.ARENA,
      label: t('HEADER.NAV.whaleDuel'),
      icon: 'fa-solid fa-people-group',
    })
  }

  if (isSkinClashEnabled.value) {
    allRoutes.push({
      name: RouteName.SKIN_CLASH,
      label: t('HEADER.NAV.skinClash'),
      icon: 'fa-solid fa-layer-group',
    })
  }

  // Whale Protocol (Safebox) - Always enabled for now
  allRoutes.push({
    name: RouteName.WHALE_PROTOCOL,
    label: t('HEADER.NAV.safebox'),
    icon: 'fa-solid fa-bomb',
  })

  if (isUpgradeEnabled.value) {
    allRoutes.push({
      name: RouteName.UPGRADE,
      label: t('HEADER.NAV.upgrade'),
      icon: 'fa-solid fa-arrow-trend-up',
    })
  }

  if (isContractsEnabled.value) {
    allRoutes.push({
      name: RouteName.CONTRACTS,
      label: t('HEADER.NAV.contracts'),
      icon: 'fa-solid fa-scroll',
    })
  }

  if (isCasesEnabled.value) {
    allRoutes.push({
      name: RouteName.CASES,
      label: t('HEADER.NAV.cases'),
      icon: 'fa-solid fa-box-open',
    })
  }

  return allRoutes
})

const gameNavRoutes = computed(() => {
  const byName = new Map(
    navRoutes.value
      .filter((route) => gameRouteNames.has(route.name))
      .map((route) => [route.name, route]),
  )
  return gameRouteOrder
    .map((name) => byName.get(name))
    .filter((route): route is { name: string; label: string; icon: string } => Boolean(route))
})
const mainNavRoutes = computed(() =>
  navRoutes.value.filter((route) => !gameRouteNames.has(route.name)),
)

// Keep the core case-opening journey visible on mobile instead of deriving
// navigation priority from the desktop information architecture.
const mobilePrimaryRouteOrder = [
  RouteName.CASES,
  RouteName.DAILY_FREE,
  RouteName.UPGRADE,
  RouteName.GIVEAWAYS,
]

const primaryNavRoutes = computed(() => {
  const byName = new Map(navRoutes.value.map((route) => [route.name, route]))
  const prioritized = mobilePrimaryRouteOrder
    .map((name) => byName.get(name))
    .filter((route): route is { name: string; label: string; icon: string } => Boolean(route))

  if (prioritized.length >= 4) return prioritized.slice(0, 4)

  const prioritizedNames = new Set(prioritized.map((route) => route.name))
  const fallbackRoutes = navRoutes.value.filter((route) => !prioritizedNames.has(route.name))
  return [...prioritized, ...fallbackRoutes].slice(0, 4)
})

const secondaryNavRoutes = computed(() => {
  const primaryNames = new Set(primaryNavRoutes.value.map((route) => route.name))
  return navRoutes.value.filter((route) => !primaryNames.has(route.name))
})

function isUpgradeRoute(routeName: string) {
  return routeName === RouteName.UPGRADE
}

// Mobile menu state
const mobileMenuOpen = ref(false)

function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

function closeMobileMenu() {
  mobileMenuOpen.value = false
}

/* === Dropdown state === */
const menuOpen = ref(false)
const menuEl = ref<InstanceType<typeof DropdownMenu> | null>(null)

const toggleEl = ref<HTMLElement | null>(null)

function toggleMenu() {
  menuOpen.value = !menuOpen.value
}

function closeMenu() {
  menuOpen.value = false
}

/* Optional: simple logout stub — adjust to your auth flow */
function handleLogout() {
  closeMenu()
  profileStore.setProfile(null) // clear local profile
}

function onDocClick(e: MouseEvent) {
  if (!menuOpen.value) return
  const t = e.target as Node

  // if clicked the toggle, ignore
  if (toggleEl.value && (toggleEl.value === t || toggleEl.value.contains(t))) return

  // if clicked inside the dropdown, ignore
  const root = menuEl.value?.rootEl
  if (root && (root === t || root.contains(t))) return

  // otherwise close
  closeMenu()
}

const openLoginModal = () => {
  appStore.setShowLoginModal(true)
}

function onKeydown(e: KeyboardEvent) {
  if (!menuOpen.value) return
  if (e.key === 'Escape') closeMenu()
}

onMounted(() => {
  // Use capture so we get the event even if something calls stopPropagation in bubble phase
  document.addEventListener('click', onDocClick, true)
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick, true)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped lang="scss">
@use 'sass:color';

.header {
  flex-grow: 0;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  background-color: $surface;
  padding: 0;
  position: relative;
  z-index: 2200;

  .header__announcement-bar {
    width: 100%;
    display: flex;
    align-items: stretch;
    gap: 1rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid color.adjust($on-surface, $alpha: -0.9);

    @include mobile {
      display: none;
    }
  }

  .header__announcement {
    width: 100%;
    background: $surface;
    border-bottom: 1px solid color.adjust($on-surface, $alpha: -0.9);
    padding: 0.35rem 0.75rem;

    @include tablet {
      display: none;
    }
  }

  &.feed-open {
    @include tablet {
      .main-navigation .routes.desktop-routes,
      .game-navigation {
        display: none;
      }
    }
  }

  .secondary-nav {
    order: 2;
    display: flex;
    align-items: center;
    gap: 1.25rem;
    flex-wrap: wrap;
    font-size: 0.68rem;
    text-transform: none;
    letter-spacing: 0.02em;
    font-weight: 600;
    color: $text-light-200;
    margin-left: auto;
    justify-content: flex-end;
    text-align: right;
    padding: 0 8px;

    @include mobile {
      justify-content: center;
      margin-left: 0;
      text-align: center;
    }

    &__link {
      color: inherit;
      text-decoration: none;
      transition: color 0.2s ease;
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      position: relative;

      &:hover {
        color: $accent-yellow;
      }

      &.hidden-mobile {
        @include mobile {
          display: none;
        }
      }

      .nav-notification-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: #ef4444;
        box-shadow: 0 0 8px rgba(239, 68, 68, 0.6);
        order: -1; // Move to left
        animation: pulseDot 2s infinite;
      }
    }
  }

  @keyframes pulseDot {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
    }
  }

  /* Announcement removed */

  .main-navigation {
    width: 100%;
    height: 4.25em;
    display: flex;
    gap: 1em;
    flex-direction: row;
    align-items: center;
    background-color: $surface;
    z-index: 2001;
    position: relative;

    @include mobile {
      height: 3.5em;
      gap: 0.5em;
      padding: 0 0.25em;
    }

    .logo {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;

      img {
        position: relative;
        display: block;
        margin-right: 1em;
        width: auto;
        height: auto;
        max-height: 100%;
        object-fit: contain;

        &.hidden {
          display: none;
        }

        @include mobile {
          display: none;
        }
      }

      .logo-desktop {
        height: 82%;
        max-height: 82%;
        align-self: center;
      }

      .logo-mobile {
        display: none;

        @include mobile {
          display: block;
          height: 2em;
          margin-right: 0;
        }
      }
    }

    nav {
      position: relative;
      display: flex;
      width: 100%;
      height: 100%;

      .routes {
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        gap: 0.8em;
        height: 100%;
        overflow: hidden;

        &.desktop-routes {
          @include mobile {
            display: none;
          }
        }

        a.route {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: $text-light-100;
          font-weight: 600;
          font-size: 0.8rem;
          text-decoration: none;
          height: 100%;
          padding: 0 0.25rem;
          border-bottom: 3px solid transparent;
          border-radius: 2px;
          transition: color 0.2s ease;

          .icon {
            display: grid;
            place-items: center;
            border-radius: 8px;
            font-size: 14px;
            color: $text-light-200;
            transition: color 0.2s ease;

            @include tablet {
              display: none;
            }
          }

          &:hover {
            color: #ffd580;

            .icon {
              color: #ffd580;
            }
          }

          &:active {
            color: #ffbe55;

            .icon {
              color: #ffbe55;
            }
          }

          &.hidden-lg {
            display: none;
          }

          &.router-link-exact-active {
            color: #ffbe55;

            .icon {
              color: #ffbe55;
            }
          }

          .route-badge {
            flex-shrink: 0;
            margin-left: -1px;

            @include tablet {
              display: none;
            }
          }
        }

        /* icon-only on mobile */
        @include mobile {
          a.route {
            padding: 0 0.75rem;

            .label {
              display: none;
            }

            .icon {
              width: 30px;
              height: 30px;
              font-size: 15px;
            }
          }
        }
      }

      .right-navigation {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        height: 100%;
        margin-left: auto;
        padding-right: 0.5em;

        .login-with-steam {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 6px;
          background-color: #145a32; // darker green base
          border: 2px solid #2ecc71; // bright green border
          color: #37f185; // text same as border
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition:
            background-color 0.25s ease,
            color 0.25s ease,
            transform 0.1s ease;
          cursor: pointer;

          i {
            font-size: 16px;
          }

          &:hover {
            background-color: #1e8449; // brighter green on hover
            color: #fff; // text/icon turn white
          }
        }

        .profile-area {
          position: relative;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;

          a,
          .profile-balance {
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: #ffffff;
            text-decoration: none;
            cursor: pointer;
          }

          .profile-name {
            max-width: 180px;
            flex-shrink: 0;
            padding: 0 0.75em;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
          }

          @include mobile {
            .profile-link {
              display: none;
            }
          }

          img.avatar {
            height: 60%;
            border: 2px solid #1f2a3a;
            border-radius: 50%;
            object-fit: cover;
            display: block;
            transition:
              border-color 0.3s ease,
              box-shadow 0.3s ease;
          }

          .profile-link {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;

            &--vip {
              .avatar {
                border-color: #ffd580;
                box-shadow:
                  0 0 0 2px rgba(255, 222, 173, 0.2),
                  0 0 12px rgba(255, 214, 128, 0.75),
                  0 0 28px rgba(255, 190, 85, 0.55);
                animation: vipPulse 2s ease-in-out infinite;
              }

              .level-badge {
                background-color: #ffd580;
                color: #1f1f1f;
              }
            }

            .level-badge {
              position: absolute;
              bottom: 5px;
              right: 50%;
              transform: translateX(50%);
              background-color: #ffd580;
              border-radius: 12px;
              border: 2px solid $surface;
              color: #1f1f1f;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 0.6em;
              font-weight: 700;
              padding: 2px 6px;
            }
          }

          .dropdown-menu-toggle {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: $text-light-100;
            font-size: 16px;
            cursor: pointer;
            user-select: none;
          }
        }
      }

      .navigation-loader {
        position: absolute;
        right: 4em;
        margin-left: auto;
        margin-top: 16px;
      }
    }
  }
}

.game-navigation {
  display: flex;
  gap: 0.35rem;
  width: 100%;
  padding: 0.5rem 0.5rem 0;
  margin: 0 0 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  @include mobile {
    display: none;
  }

  .game-route {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 0.45rem 0.7rem;
    flex: 1 1 0;
    justify-content: center;
    color: $text-light-100;
    font-weight: 700;
    letter-spacing: 0.01em;
    font-size: 0.85rem;
    text-decoration: none;
    border-radius: 8px;
    position: relative;
    overflow: hidden;
    border: 1px solid transparent;
    transition:
      border-color 0.2s ease,
      color 0.2s ease,
      transform 0.2s ease;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(circle at 20% 50%, rgba(255, 214, 128, 0.12), transparent 45%);
      opacity: 0;
      transition: opacity 0.2s ease;
      pointer-events: none;
    }

    &:hover {
      border-color: rgba(255, 214, 128, 0.55);
      color: #ffd680;

      &::after {
        opacity: 1;
      }
    }

    &.router-link-exact-active {
      border-color: #ffd580;
      color: #ffd580;

      &::after {
        opacity: 1;
      }
    }

    .icon {
      color: inherit;
      font-size: 15px;
    }

    .label {
      font-size: 0.82rem;
    }

    .route-badge {
      flex-shrink: 0;
      margin-left: -2px;
    }
  }
}

// Mobile Bottom Navigation
.mobile-bottom-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: $surface;
  height: 60px;
  padding-bottom: env(safe-area-inset-bottom);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
  z-index: 2000;
  justify-content: space-around;
  align-items: stretch;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @include mobile {
    display: flex;
  }

  &.feed-open {
    @include tablet {
      display: flex;
    }
  }

  .mobile-nav-more {
    background: none;
    border: none;
    cursor: pointer;
  }

  .mobile-nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex: 1;
    min-width: 0;
    color: $text-light-100;
    text-decoration: none;
    font-size: 0.7rem;
    font-weight: 500;
    transition: color 0.2s ease;
    position: relative;
    -webkit-tap-highlight-color: transparent;

    .icon {
      font-size: 20px;
      color: $text-light-200;
      transition:
        color 0.2s ease,
        transform 0.2s ease;
    }

    .label {
      text-align: center;
      line-height: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
      transition: color 0.2s ease;
    }

    .mobile-route-badge {
      position: absolute;
      top: 5px;
      right: 8px;
      max-width: 42px;
      font-size: 0.46rem;
    }

    &:hover {
      color: #ffd580;

      .icon {
        color: #ffd580;
      }
    }

    &:active {
      color: #ffbe55;

      .icon {
        color: #ffbe55;
      }
    }

    &.router-link-exact-active {
      color: #ffbe55;

      .icon {
        color: #ffbe55;
        transform: scale(1.1);
      }

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: #ffbe55;
      }
    }
  }
}

// Mobile Menu Overlay
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 2500;
  display: flex;
  align-items: flex-end;
  backdrop-filter: blur(4px);
}

.mobile-menu-content {
  width: 100%;
  background-color: $surface;
  border-radius: 16px 16px 0 0;
  padding: 1.5rem;
  padding-bottom: calc(1.5rem + env(safe-area-inset-bottom));
  max-height: 70vh;
  overflow-y: auto;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.5);
}

.mobile-menu-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    color: $text-light-100;
    margin: 0;
  }

  .close-btn {
    background: none;
    border: none;
    color: $text-light-100;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;
    -webkit-tap-highlight-color: transparent;

    &:hover,
    &:active {
      color: #ffd580;
    }
  }
}

.mobile-menu-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  color: $text-light-100;
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 600;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
  -webkit-tap-highlight-color: transparent;

  .icon {
    font-size: 1.25rem;
    color: $text-light-200;
    width: 24px;
    text-align: center;
    transition: color 0.2s ease;
  }

  .label {
    flex: 1;
  }

  .mobile-menu-badge {
    flex-shrink: 0;
  }

  &:active {
    background-color: rgba(255, 214, 128, 0.15);
    color: #ffd580;

    .icon {
      color: #ffd580;
    }
  }

  &.router-link-exact-active {
    background-color: rgba(255, 214, 128, 0.1);
    color: #ffbe55;

    .icon {
      color: #ffbe55;
    }
  }
}

// Mobile Menu Transitions
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.3s ease;

  .mobile-menu-content {
    transition: transform 0.3s ease;
  }
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;

  .mobile-menu-content {
    transform: translateY(100%);
  }
}

@keyframes vipPulse {
  0% {
    box-shadow:
      0 0 0 1px rgba(255, 222, 173, 0.2),
      0 0 8px rgba(255, 214, 128, 0.65),
      0 0 18px rgba(255, 190, 85, 0.45);
  }

  50% {
    box-shadow:
      0 0 0 2px rgba(255, 222, 173, 0.35),
      0 0 12px rgba(255, 214, 128, 0.85),
      0 0 24px rgba(255, 190, 85, 0.65);
  }

  100% {
    box-shadow:
      0 0 0 1px rgba(255, 222, 173, 0.2),
      0 0 8px rgba(255, 214, 128, 0.65),
      0 0 18px rgba(255, 190, 85, 0.45);
  }
}
</style>
